import { useCallback, useMemo } from 'react';
import { getLastDismissAt, recordDismiss, BannerType } from 'utils/banner';
interface UseTelematicsBannerParams {
  equipmentId: string;
  isSubscriptionsFeatureEnabled: boolean;
  isBasicTelematics: boolean;
  expiresSoon: boolean;
  ttlDays?: number;
}
interface TelematicsBannerState {
  showExpiresSoon: boolean;
  showUnlockFeatures: boolean;
  dismissExpiresSoon: () => void;
  dismissUnlockFeatures: () => void;
}

const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;
const DEFAULT_TTL_DAYS = 30;

export function useTelematicsBanner({
  equipmentId,
  isSubscriptionsFeatureEnabled,
  isBasicTelematics,
  expiresSoon,
  ttlDays = DEFAULT_TTL_DAYS,
}: UseTelematicsBannerParams): TelematicsBannerState {
  const shouldShowBanner = useCallback(
    (bannerType: BannerType, isEligible: boolean): boolean => {
      if (!isEligible || !equipmentId) return false;

      const lastDismissedAt = getLastDismissAt(equipmentId, bannerType);
      if (!lastDismissedAt) return true;

      const timeSinceDismissal = Date.now() - lastDismissedAt;
      return timeSinceDismissal > ttlDays * MILLISECONDS_PER_DAY;
    },
    [equipmentId, ttlDays],
  );

  const showExpiresSoon = useMemo(
    () =>
      shouldShowBanner(
        BannerType.SUBSCRIPTION_EXPIRES_SOON,
        expiresSoon && isSubscriptionsFeatureEnabled,
      ),
    [shouldShowBanner, expiresSoon, isSubscriptionsFeatureEnabled],
  );

  const showUnlockFeatures = useMemo(
    () =>
      shouldShowBanner(
        BannerType.SUBSCRIPTION_UNLOCK_FEATURES,
        isBasicTelematics && isSubscriptionsFeatureEnabled,
      ),
    [shouldShowBanner, isBasicTelematics, isSubscriptionsFeatureEnabled],
  );

  const dismissExpiresSoon = useCallback(() => {
    recordDismiss(equipmentId, BannerType.SUBSCRIPTION_EXPIRES_SOON);
  }, [equipmentId]);

  const dismissUnlockFeatures = useCallback(() => {
    recordDismiss(equipmentId, BannerType.SUBSCRIPTION_UNLOCK_FEATURES);
  }, [equipmentId]);

  return {
    showExpiresSoon,
    showUnlockFeatures,
    dismissExpiresSoon,
    dismissUnlockFeatures,
  };
}
